import React, { useState, useEffect } from "react";
import InputComponent from "../commonInputBox/InputBox";
import Accordion from "../../Accordian/Accordian";
import style from "../Information/style.module.css";
import { getAllLanguages, getCountry, getGeo, getLanguage, getTaxonomyByType } from "../../api/HttpApis";
import { useLocation } from "react-router-dom";
import Button from "../button/Button";
import AiGeneratedBadge from "../aiAssist/AiGeneratedBadge";

const getHash = () => {
  try {
    return typeof window !== "undefined" ? window.location.hash : "";
  } catch {
    return "";
  }
};

const BasicInformation = ({
  nameFromState,
  formData,
  handleChange,
  isEditing,
  categoriesData,
  seriesList,
  seasonList,
  genresDataList,
  ratingOptions,
  accessOptions,
  eventData,
  streamData,
  statusData,
  mediaLibrary,
  soloProps,
  isSeason,
  isSeries,
  aiFields,
  durationAutoFilled,
  durationPrefilled,
}) => {
  // True when the given form field currently holds an AI-generated value.
  const isAi = (key) => Boolean(aiFields && aiFields.has && aiFields.has(key));
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedBadges, setSelectedBadges] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [languageList, setLanguageList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [badgeList, setBadgeList] = useState([]);
  const [contentWarningList, setContentWarningList] = useState([]);
  const [seriesFormatList, setSeriesFormatList] = useState([]);

  const hash = getHash();
  const isEpisodeAdd = hash.split("/").length >= 5 && hash.includes("/video/");
  const isField = hash.includes("/add") || isEpisodeAdd;
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const urlEndpoint = pathSegments[pathSegments.length - 1];

  useEffect(() => {
    if (isEditing) return;
    if (!categoriesData?.length) return;
    const nameMap = {
      "movie": "Movie",
      "creator-movie": "Movie",
      "podcasts": "Podcast",
      "short-clips": "Short Clip",
      "series": "Series",
      "episodes": "Series",
      "live-streams": "Live Stream",
      "Live Channels": "Live Channel",
      "Live Events": "Live Event",
    };
    const mappedName = isEpisodeAdd ? "Series" : (nameFromState ? nameMap[nameFromState] : null);

    const matched = mappedName
      ? categoriesData.find((c) => c.name === mappedName)
      : categoriesData.find(
        (c) => c.name.toLowerCase() === urlEndpoint.toLowerCase()
      );

    if (matched && formData?.["content-type"] !== matched.name) {
      handleChange({
        target: { name: "content-type", value: matched.name },
      });
    }
  }, [nameFromState, urlEndpoint, categoriesData, isEpisodeAdd, isEditing]);

  useEffect(() => {
    if (isEditing) return;
    if (!urlEndpoint || !categoriesData?.length) return;

    const matched = categoriesData.find(
      (c) => c.name.toLowerCase() === urlEndpoint.toLowerCase()
    );
    if (matched && formData?.["content-type"] !== matched.name) {
      handleChange({
        target: { name: "content-type", value: matched.name },
      });
    }
  }, [urlEndpoint, categoriesData, isEditing]);

  useEffect(() => {
    getCountry()
      .then((data) => setCountryList(Array.isArray(data) ? data : []))
      .catch(() => setCountryList([]));

    getAllLanguages()
      .then((res) => {
        const list = res?.data?.data;
        if (!Array.isArray(list)) return setLanguageList([]);
        const mapped = list.map((item) => ({
          code: item.value,
          name: item.key.charAt(0) + item.key.slice(1).toLowerCase(),
        }));
        setLanguageList(mapped);
      })
      .catch(() => setLanguageList([]));

    getTaxonomyByType("BADGE")
      .then((res) => {
        const list = res?.data?.data;
        if (!Array.isArray(list)) return setBadgeList([]);
        setBadgeList(list.map((item) => item.value ?? item.name ?? item.key));
      })
      .catch(() => setBadgeList([]));

    getTaxonomyByType("CONTENT_WARNING")
      .then((res) => {
        const list = res?.data?.data;
        if (!Array.isArray(list)) return setContentWarningList([]);
        setContentWarningList(
          list.map((item) => item.value ?? item.name ?? item.key)
        );
      })
      .catch(() => setContentWarningList([]));

    getTaxonomyByType("SERIES_FORMAT")
      .then((res) => {
        const list = res?.data?.data;
        if (!Array.isArray(list)) return setSeriesFormatList([]);
        setSeriesFormatList(list.map((item) => item.value ?? item.name ?? item.key));
      })
      .catch(() => setSeriesFormatList([]));
  }, []);

  useEffect(() => {
    if (!formData?.genres || !genresDataList?.length) return;
    const incoming = Array.isArray(formData.genres)
      ? formData.genres
      : formData.genres.split(",").map((g) => g.trim()).filter(Boolean);
    const ids = incoming?.map((nameOrId) => {
      const found = genresDataList.find(
        (g) =>
          g.name.toLowerCase() === String(nameOrId).toLowerCase() ||
          String(g.id) === String(nameOrId)
      );
      return found ? String(found.id) : null;
    }).filter(Boolean);
    if (ids.length > 0) setSelectedGenres(ids);
  }, [formData?.genres, genresDataList]);

  useEffect(() => {
    if (!formData?.badges) {
      setSelectedBadges([]);
      return;
    }
    const incoming = Array.isArray(formData.badges)
      ? formData.badges
      : formData.badges.split(",")?.map((b) => b.trim()).filter(Boolean);
    if (incoming.length > 0) {
      setSelectedBadges([incoming[0]]);
    } else {
      setSelectedBadges([]);
    }
  }, [formData?.badges]);

  useEffect(() => {
    const isLive =
      formData["content-type"] === "Live Channel" ||
      formData["content-type"] === "Live Event";

    if (!isLive || !genresDataList?.length) return;

    const liveGenre = genresDataList.find(
      (g) => g.name.toLowerCase() === "live"
    );
    if (!liveGenre) return;

    const liveId = String(liveGenre.id);

    setSelectedGenres((prev) => {
      if (prev.includes(liveId)) return prev;
      const updated = [...prev, liveId];
      const names = updated.map(
        (id) => genresDataList.find((g) => String(g.id) === id)?.name ?? id
      );
      handleChange({
        target: { name: "genres", value: names.join(",") },
      });
      return updated;
    });
  }, [formData["content-type"], genresDataList]);

  // Prefill defaults for new entries: Language (English), Rating (minimum/default)
  useEffect(() => {
    if (!formData || isEditing) return;

    if (!formData.language && languageList?.length) {
      const englishLanguage = languageList.find((l) => l.name?.toLowerCase() === "english");
      if (englishLanguage) {
        handleChange({ target: { name: "language", value: englishLanguage.code } });
      }
    }

    if (!formData.rating && ratingOptions?.length) {
      handleChange({ target: { name: "rating", value: ratingOptions[0].name } });
    }
  }, [isEditing, languageList, ratingOptions]);

  // Auto-fill Country from the user's real location (IP geo, server-side) for new
  // entries. Fires once the country list is loaded, only when Country is still
  // empty — a manual pick or an edit is never overridden. On localhost / unknown
  // IP the backend returns null country and we leave the field for manual choice.
  useEffect(() => {
    if (isEditing || !countryList?.length || formData?.country) return;
    let cancelled = false;
    getGeo().then((geo) => {
      const code = geo?.country; // ISO alpha-2, e.g. "IN"
      if (cancelled || !code) return;
      if (countryList.some((c) => c.IsoCode === code)) {
        handleChange({ target: { name: "country", value: code } });
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, countryList]);

  if (!formData) return null;

  const isLive =
    formData["content-type"] === "Live Channel" ||
    formData["content-type"] === "Live Event";

  const toggleGenre = (genreId) => {
    setSelectedGenres((prev) => {
      const strId = String(genreId);
      const updated = prev.includes(strId)
        ? prev.filter((g) => g !== strId)
        : [...prev, strId];
      const names = updated.map(
        (id) => genresDataList.find((g) => String(g.id) === id)?.name ?? id
      );
      handleChange({
        target: {
          name: "genres",
          value: names.join(","),
        },
      });
      return updated;
    });
  };

  const toggleBadge = (badge) => {
    setSelectedBadges((prev) => {
      const isAlreadySelected = prev.includes(badge);
      const updated = isAlreadySelected ? [] : [badge];

      handleChange({
        target: {
          name: "badges",
          value: updated[0] || "",
        },
      });
      return updated;
    });
  };

  const titleMissing = submitted && !formData.name;

  return (
    <Accordion
      title="Basic Information"
      icon={<img src={`${process.env.PUBLIC_URL}/basicInformation.png`} alt="" width={18} height={18} />}
      openId="basic"
      {...soloProps}
    >
      <div className={style.basicWrapper}>

        {/* ── Row 1: Title + Original Title + Tagline ── */}
        <div className={style.basicThreeRow}>

          <div className={style.basicGroup}>
            <label className={style.basicLabel}>
              Title <span style={{ color: "var(--app-primary, #B87333)" }}>*</span>
              {isAi("name") && <AiGeneratedBadge size="sm" />}
            </label>
            <input
              name="name"
              maxLength={100}
              className={`${style.basicInput} ${titleMissing ? style.basicInputError : ""}`}
              placeholder="Official Content Title"
              value={formData.name || ""}
              onChange={handleChange}
              onBlur={() => setSubmitted(true)}
            />
            {titleMissing && (
              <span className={style.basicErrorText}>This field is required</span>
            )}
          </div>

          <>
            {(!isSeries && !hash.includes("/add")) && !hash.includes("/add") ? (
              <div className={style.basicGroup}>
                <label className={style.basicLabel}>
                  Content Type <span style={{ color: "var(--app-primary, #B87333)" }}>*</span>
                </label>
                <select
                  name="content-type"
                  className={style.basicSelect}
                  value={formData["content-type"] || ""}
                  onChange={handleChange}
                  // Episodes are always "Series" — lock it on episode add/edit too
                  // (isEditing is false on episode routes, so it doesn't cover them).
                  disabled={isEditing || isEpisodeAdd}
                >
                  <option value="">Select</option>
                  {(categoriesData || [])?.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {isSeries && (
              <div className={style.basicGroup}>
                <label className={style.basicLabel}>
                  Series Format <span style={{ color: "var(--app-primary, #B87333)" }}>*</span>
                </label>
                <select
                  name="series_format"
                  className={style.basicSelect}
                  value={formData.series_format || ""}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  {seriesFormatList.map((format) => (
                    <option key={format} value={format}>{format}</option>
                  ))}
                </select>
              </div>
            )}

            {!isField && (
              <div className={style.basicGroup}>
                <label className={style.basicLabel}>
                  Tagline
                  {isAi("tagline") && <AiGeneratedBadge size="sm" />}
                </label>
                <input
                  name="tagline"
                  maxLength={120}
                  className={style.basicInput}
                  placeholder='"One Line That Sells It"'
                  value={formData.tagline || ""}
                  onChange={handleChange}
                />
              </div>
            )}
          </>

          {/* Special view for Episode Route */}
          {isEpisodeAdd && (
            <>
              <div className={style.basicGroup}>
                <label className={style.basicLabel}>Episode Number</label>
                <input
                  type="number"
                  name="episode_number"
                  min={1}
                  className={style.basicInput}
                  value={formData.episode_number || ""}
                  onChange={handleChange}
                />
              </div>
            </>
          )}
        </div>

        {/* ── Season Number field (Visible for Seasons Add) ── */}
        {hash.includes("/add") && !isSeries && !isEpisodeAdd && (
          <div className={style.basicTwoRow}>
            <div className={style.basicGroup}>
              <label className={style.basicLabel}>
                Season Number <span style={{ color: "var(--app-primary, #B87333)" }}>*</span>
              </label>
              <input
                type="number"
                name="season_number"
                className={style.basicInput}
                min={1}
                placeholder="1"
                value={formData.season_number || ""}
                onChange={handleChange}
              />
            </div>
          </div>
        )}

        {!isField && (
          <>

            <div className={style.basicGroup}>
              <label className={style.basicLabel}>
                Description <span style={{ color: "var(--app-primary, #B87333)" }}>*</span>
                {isAi("description") && <AiGeneratedBadge size="sm" />}
              </label>
              <textarea
                name="description"
                className={style.basicTextarea}
                placeholder="Quick Teaser..."
                value={formData.description || ""}
                onChange={handleChange}
                maxLength={500}
              />
            </div>

            {!isLive && (
              <div className={style.basicThreeRow}>
                <div className={style.basicGroup}>
                  <label className={style.basicLabel}>
                    Release Year
                    {isAi("year") && <AiGeneratedBadge size="sm" />}
                  </label>
                  <select name="year" className={style.basicSelect} value={formData.year || ""} onChange={handleChange}>
                    <option value="">Select</option>
                    {Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div className={style.basicGroup}>
                  <label className={style.basicLabel}>Release Date</label>
                  <input type="date" name="release_date" className={style.basicInput} value={formData.release_date || ""} onChange={handleChange} />
                </div>
                {!isSeries && (
                  <div className={style.basicGroup}>
                    <label className={style.basicLabel}>
                      Duration
                      {isAi("duration") && <AiGeneratedBadge size="sm" />}
                    </label>
                    {/* Text + numeric keypad instead of type="number" so there are no
                        up/down spinner arrows. Input is normalised to whole seconds:
                        whitespace trimmed, anything after a decimal point dropped
                        ("5400.5" -> "5400"), other non-digits stripped. */}
                    <input
                      type="text"
                      inputMode="numeric"
                      name="duration"
                      className={style.basicInput}
                      placeholder="e.g. 7200"
                      value={formData.duration || ""}
                      disabled={durationPrefilled}
                      style={durationPrefilled ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        const value = raw.split(".")[0].replace(/\D/g, "");
                        handleChange({ target: { name: "duration", value } });
                      }}
                    />
                    <span className={style.basicLabelNote}>
                      {durationAutoFilled
                        ? "Duration should be in seconds (auto-filled from the video file)."
                        : "Duration should be in seconds."}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className={style.basicThreeRow}>
              <div className={style.basicGroup}>
                <label className={style.basicLabel}>
                  Rating <span style={{ color: "var(--app-primary, #B87333)" }}>*</span>
                  {isAi("rating") && <AiGeneratedBadge size="sm" />}
                </label>
                <select name="rating" className={style.basicSelect} value={formData.rating || ""} onChange={handleChange}>
                  <option value="">Select</option>
                  {ratingOptions?.map((o) => (
                    <option key={o.id} value={o.name}>{o.name}</option>
                  ))}
                </select>
              </div>
              <div className={style.basicGroup}>
                <label className={style.basicLabel}>
                  Language <span style={{ color: "var(--app-primary, #B87333)" }}>*</span>
                  {isAi("language") && <AiGeneratedBadge size="sm" />}
                </label>
                <select name="language" className={style.basicSelect} value={formData.language || ""} onChange={handleChange}>
                  <option value="">Select</option>
                  {languageList?.map((l) => (
                    <option key={l.code} value={l.code}>{l.name}</option>
                  ))}
                </select>
              </div>
              <div className={style.basicGroup}>
                <label className={style.basicLabel}>Country</label>
                <select name="country" className={style.basicSelect} value={formData.country || ""} onChange={handleChange}>
                  <option value="">Select</option>
                  {countryList?.map((c) => (
                    <option key={c.IsoCode} value={c.IsoCode}>{c.Name}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {/* ── Genres & Badge — visible for ALL content types including episodes ── */}
        {!hash.includes("/add") && !isLive && (
          <>
            <div className={style.basicGroup}>
              <label className={style.basicLabel}>
                Genres
                {isAi("genres") && <AiGeneratedBadge size="sm" />}
              </label>
              <div className={style.basicGenreRow}>
                {genresDataList?.map((genre) => (
                  <Button
                    key={genre.id}
                    type="button"
                    className={`${style.basicGenreTag} ${selectedGenres.includes(String(genre.id)) ? style.basicGenreTagActive : ""}`}
                    onClick={() => toggleGenre(genre.id)}
                  >
                    {genre.name}
                  </Button>
                ))}
              </div>
            </div>
            {!isField && (
              <div className={style.basicGroup}>
                <label className={style.basicLabel}>Badge</label>
                <div className={style.basicGenreRow}>
                  {badgeList?.map((badge) => (
                    <Button
                      key={badge}
                      type="button"
                      className={`${style.basicGenreTag} ${selectedBadges.includes(badge) ? style.basicGenreTagActive : ""}`}
                      onClick={() => toggleBadge(badge)}
                    >
                      {badge}
                    </Button>
                  ))}
                </div>
                <div className={style.basicThreeRow}>
                  <div className={style.basicGroup}>
                    <label className={style.basicLabel}>Studio</label>
                    <input
                      name="studio"
                      maxLength={80}
                      className={style.basicInput}
                      placeholder="e.g. A24"
                      value={formData.studio || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={style.basicGroup}>
                    <label className={style.basicLabel}>IMDB ID</label>
                    <input
                      name="imdb_id"
                      maxLength={20}
                      className={style.basicInput}
                      placeholder="IMDB ID"
                      value={formData.imdb_id || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={style.basicGroup}>
                    <label className={style.basicLabel}>
                      Content Warnings
                      {isAi("content_warning") && <AiGeneratedBadge size="sm" />}
                    </label>
                    <select
                      name="content_warning"
                      className={style.basicSelect}
                      value={formData.content_warning || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      {contentWarningList?.map((warning) => (
                        <option key={warning} value={warning}>{warning}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Live Fields ── */}
        {isLive && (
          <>
            <div className={style.basicThreeRow}>
              {formData["content-type"] === "Live Channel" ? (
                <div className={style.basicGroup}>
                  <label className={style.basicLabel}>
                    Live Status <span style={{ color: "var(--app-primary, #B87333)" }}>*</span>
                  </label>
                  <select name="liveStatus" className={style.basicSelect} value={formData.liveStatus || ""} onChange={handleChange}>
                    <option value="">Select</option>
                    {statusData?.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div className={style.basicGroup}>
                    <label className={style.basicLabel}>
                      Event Schedule <span style={{ color: "var(--app-primary, #B87333)" }}>*</span>
                    </label>
                    <select name="schedule_type" className={style.basicSelect} value={formData.schedule_type || ""} onChange={handleChange}>
                      <option value="">Select</option>
                      <option value="Instant">Instant</option>
                      <option value="Scheduled">Scheduled</option>
                    </select>
                  </div>
                  <div className={style.basicGroup}>
                    <label className={style.basicLabel}>
                      Live Status <span style={{ color: "var(--app-primary, #B87333)" }}>*</span>
                    </label>
                    <select name="liveStatus" className={style.basicSelect} value={formData.liveStatus || ""} onChange={handleChange}>
                      <option value="">Select</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </>
              )}
            </div>

            {formData.schedule_type?.toLowerCase()?.trim() === "scheduled" && (
              <div className={style.basicThreeRow}>
                <div className={style.basicGroup}>
                  <label className={style.basicLabel}>Start Time</label>
                  <input type="datetime-local" name="start_time" className={style.basicInput} value={formData.start_time || ""} onChange={handleChange} />
                </div>
                <div className={style.basicGroup}>
                  <label className={style.basicLabel}>End Time</label>
                  <input type="datetime-local" name="end_time" className={style.basicInput} value={formData.end_time || ""} onChange={handleChange} />
                </div>
              </div>
            )}

            <div className={style.basicGroup}>
              <label className={style.basicLabel}>
                Genres
                {isAi("genres") && <AiGeneratedBadge size="sm" />}
              </label>
              <div className={style.basicGenreRow}>
                {genresDataList?.map((genre) => (
                  <Button
                    key={genre.id}
                    type="button"
                    className={`${style.basicGenreTag} ${selectedGenres.includes(String(genre.id)) ? style.basicGenreTagActive : ""}`}
                    onClick={() => toggleGenre(genre.id)}
                  >
                    {genre.name}
                  </Button>
                ))}
              </div>
            </div>
          </>
        )}

      </div>
    </Accordion>
  );
};

export default BasicInformation;